import type { AutomationDProp } from '@tms/shared';
import { Copy, Terminal, Zap } from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Table,
  TableWrap,
  Td,
  Th,
} from '../../../components/ui';
import { useT } from '../../../i18n';

/** The command block: what the GitLab Runner will execute, and its -D parameters. */
export const CommandPreview = ({
  command,
  dProps,
  fromRepo,
  gradleTask,
}: {
  command: string;
  dProps: AutomationDProp[];
  /** the task and the parameters come from a connected repository */
  fromRepo: boolean;
  gradleTask: string;
}) => {
  const { t } = useT();
  const copy = () => {
    void navigator.clipboard?.writeText(command);
    toast.success(t('automation.runs.command.copied'));
  };
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Terminal className="size-4" aria-hidden="true" />
            {t('automation.runs.command.title')}
          </span>
        }
        action={
          <Button size="sm" onClick={copy}>
            <Copy className="size-3.5" aria-hidden="true" />
            {t('automation.runs.command.copy')}
          </Button>
        }
      />
      <CardBody className="space-y-3">
        <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-lg bg-sunken p-3.5 font-mono text-[12.5px] leading-relaxed text-ink">
          {command}
        </pre>
        <p className="text-[12px] text-ink-subtle">
          {fromRepo
            ? t('automation.runs.command.fromRepo', { task: gradleTask })
            : t('automation.runs.command.fromData')}
        </p>
      </CardBody>
      {dProps.length > 0 ? (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>{t('automation.runs.command.prop')}</Th>
                <Th>{t('automation.runs.command.value')}</Th>
                <Th>{t('automation.runs.command.ciVar')}</Th>
                <Th className="w-28">{t('automation.runs.command.passing')}</Th>
              </tr>
            </thead>
            <tbody>
              {dProps.map((p) => (
                <tr key={p.key}>
                  <Td className="font-mono font-semibold">{p.key}</Td>
                  <Td className="break-all font-mono text-ink-muted">{p.value}</Td>
                  <Td className="font-mono text-ink-muted">{p.ciVar}</Td>
                  <Td>
                    {p.wiredInCi ? (
                      <Badge
                        className="bg-pass-soft text-pass"
                        title={t('automation.runs.command.ciBadgeHint')}
                      >
                        <Zap className="size-3" aria-hidden="true" />
                        {t('automation.runs.command.ciBadge')}
                      </Badge>
                    ) : (
                      <Badge title={t('automation.runs.command.dBadgeHint')}>-D</Badge>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      ) : null}
    </Card>
  );
};
